import * as THREE from "three";
import { MATS } from "../materials.js";
import * as H from "./spec.js";

/**
 * The stair, and the mirrored closet in the hall on the way to it.
 *
 * The stair climbs the GARAGE WALL -- PLAN.divX, the full-depth partition
 * the plan already draws -- and that is why this file is shorter than it
 * was: interior.js builds that wall, so there is no wall to invent here.
 * STAIR.wall is read for one thing only, which side the balustrade is NOT
 * on.
 *
 * You come in the front door, cross the hall, take three risers up to your
 * right on to a quarter landing, turn left, and eleven treads run away from
 * you up the wall to the boards upstairs.js lays.  Two flights at right
 * angles: the low one climbs +X and the long one climbs -Z.  Nothing below
 * is written twice for that -- every piece of a step is placed in (u, w),
 * along the climb and across it, and one `dir` says which way along.
 *
 * Every number comes out of spec.STAIR, spec.STAIRWELL and spec.CLOSET, and
 * nothing here re-derives one.  That is the whole point of those exports:
 * the hole upstairs.js cuts in the floor, the hole interior.js cuts in the
 * ceiling, and the flight that climbs through both are one rectangle read
 * three times.
 *
 * Fifteen risers of 0.1967 carry 0.55 to 3.50 -- two treads and a landing
 * off the hall, eleven up the wall, and one last riser onto the boards.
 * player.js steps up 0.23 m without being asked to jump, so at 0.197 the cat
 * WALKS the whole stair, and the colliders below are laid so it never has to
 * notice one.  None of those numbers is typed into the code: they are
 * STAIR.rise and STAIR.flight.treads, and the stair has now moved three
 * times under this file -- west, a riser shorter, and on to a different wall
 * with its axes swapped -- without either arithmetic or a collider changing.
 *
 * Collision, which is the part worth reading:
 *
 *   - the low flight and the landing are SOLID to the floor.  They stand out
 *     in the hall with their sides showing, and the half-metre void under a
 *     quarter-space landing is exactly the hole a cat gets wedged into --
 *     the same call deck.js made under its own turn platform.
 *   - the long flight is NOT.  One box per tread, its top the tread level
 *     and its bottom exactly one rise below, so the boxes tile the flight
 *     with no gap and no lip while the space beneath stays open: that is
 *     dining-room floor, and the understairs is somewhere to walk.
 *   - the balustrades carry nothing at all.  A handrail is trim, and a cat
 *     caught on invisible trim is the bug this level keeps not having.  The
 *     one rail that might have been a perch is the raked one, which is also
 *     the one hanging over a 2.36 m drop onto the landing, so it stays
 *     visual and the cat can neither balance on it nor snag on it.
 *
 * And what faces DOWN, which is the other half of the same discipline.  The
 * scene's fill is a hemisphere whose lower half is lawn-coloured, so every
 * horizontal face that looks at the floor comes out olive unless it is
 * MATS.ceiling -- the trap interior.js calls out for its door headers, and
 * one this file walked straight into.  Three faces here are big enough for
 * a player to catch it: the whole underside of the long flight, which is
 * why it has a real raking soffit and why the strings are cut to bury their
 * own bottoms inside it; the closet shelf, which a cat standing in the open
 * bay looks straight up at; and every rail that hangs over the well.  The
 * last two wear a skin of ceiling material; the flight gets the plaster
 * plane a real staircase has.
 */

const S = H.STAIR;
const W = H.STAIRWELL;
const CL = H.CLOSET;
const UP = H.UP;

const F = H.INSIDE.floor;                // 0.55, the ground floor
const RISE = S.rise;                     // 0.196667
const GOING = S.going;                   // 0.28
const TAN = RISE / GOING;                // the pitch as a gradient
const PITCH = Math.atan2(RISE, GOING);   // and as an angle, 35.1 degrees

const PL = MATS.plaster;
const PC = MATS.ceiling;                 // downward faces, indoors
const TR = MATS.trim;
const TM = MATS.timber;
const FB = MATS.floorBoard;
const FS = MATS.floorShade;
const MP = MATS.metalPale;

// interior.js's indoor opts, and for the same reason: one box per run means
// per-piece tint reads as banding rather than variation, and what little AO
// is left is only there to keep a board from floating off what it sits on.
const FLAT = { solid: false, ao: 0.04, tint: 0 };
const BOARD = { solid: false, ao: 0.04, tint: 0, tile: 0.9 };

const TREAD_T = 0.05;                    // tread board
const RISER_T = 0.03;                    // riser board
const RISER_OUT = 0.008;                 // planted proud of the carriage
const NOSING = 0.025;                    // tread oversails the riser face
const STRING_T = 0.05;                   // string board, across the flight
const STRING_UP = 0.05;                  // top edge, above the nosing line
const SOFFIT_T = 0.05;                   // the plaster plane under the flight
// Its top surface, below the nosing line.  A rise plus a board is the line
// through the back corner of every step: any less and the plane cuts up
// through the treads, any more and each corner opens a slot.
const SOFFIT_DROP = RISE + TREAD_T - 0.006;
// And the string is cut exactly deep enough to bury its own underside in
// that plaster -- 8 mm short of its bottom face.  Derived rather than typed,
// because the day the riser height moves, a fixed depth would leave 3 m of
// downward-facing trim hanging below the soffit, which is the whole bug.
const STRING_D = STRING_UP + SOFFIT_DROP + SOFFIT_T - 0.008;
const RAIL_H = 0.90;                     // handrail over nosings and floor
const POST = 0.09;                       // newel section
const BAL = 0.028;                       // baluster section
const BAL_PITCH = 0.115;
const CAP = 0.06;                        // newel cap, above the rail
// A face that looks at the floor gets 16 mm of MATS.ceiling under it, 2 mm
// proud at the sides and buried 2 mm into the piece above, so no trim edge
// shows round it and no two faces share a plane.  See the file header for
// why anything that looks down needs this at all.
const SKIN = 0.016;

/** The flight's nosing line: 3.50 at the head, by definition. */
const noseY = (z) => S.top - (z - S.flight.head) * TAN;
const railY = (z) => noseY(z) + RAIL_H;
const soffitY = (z) => noseY(z) - SOFFIT_DROP;

// The one plane the open side lives in -- raked rail, the level rail above
// it, both strings' logic and every newel.  Half a post inside the well's
// west edge, so the posts' outer faces are flush with the trimmer.  The
// landing's own guard is the same idea one turn earlier, half a post inside
// the face the hall sees.
const GUARD_X = W.x0 + POST / 2;         // 0.495
const GUARD_Z = W.z1 - POST / 2;         // -3.045
const GUARD_Z_LOW = S.landing.z1 - POST / 2;   // -1.805

/**
 * The mirror, built out of what the registry already has.
 *
 * materials.js has no mirror and is nobody's to change for one closet, so
 * the leaves get a local clone of `glass`.  A cloned material costs exactly
 * one more merged batch and nothing else, which is the cheapest way to add a
 * surface the shared palette does not carry.
 *
 * The numbers are deliberately not the physical ones.  A real mirror is
 * metalness 1 at roughness 0, and in this hall it would read black: a full
 * metal has no diffuse term at all, so the only indirect light it can see is
 * the environment -- and the renderer pins that to scene.environmentIntensity
 * (0.32, see materials.useEnvironment) for every material without an envMap
 * of its own, while the sun never gets past the front door.  So the panel
 * keeps a quarter of its dielectric diffuse, which is what lets the
 * hemisphere fill light it, and a little emissive so it can never fall
 * darker than the plaster it is set into.  envMapIntensity is written for
 * the day something hands this material its own envMap; today it does
 * nothing at all.
 */
const MIRROR = MATS.glass.clone();
MIRROR.name = "mirror";
MIRROR.color.setHex(0xc9d3d8);
MIRROR.roughness = 0.06;
MIRROR.metalness = 0.75;
MIRROR.emissive.setHex(0x1c2226);
MIRROR.envMapIntensity = 2.4;

// ---------------------------------------------------------------------------
// Flight-local placement.  `u` runs the way the flight climbs and `w` across
// it, so the two flights -- one climbing +X off the hall, one climbing -Z up
// the wall -- are the same six lines with the axis swapped.  interior.js
// plays the same trick with its wall-local (u, t).
function along(b, axis, uc, y, wc, ul, h, wl, mat, opts = FLAT) {
  if (axis === "x") b.block(uc, y, wc, ul, h, wl, mat, opts);
  else b.block(wc, y, uc, wl, h, ul, mat, opts);
}

function alongCollider(b, axis, uc, y, wc, ul, h, wl) {
  if (axis === "x") b.collider(uc, y, wc, ul, h, wl);
  else b.collider(wc, y, uc, wl, h, ul);
}

/**
 * One step: a board with a nosing, the riser under it, and one collider.
 *
 * `dir` is which way along u the flight climbs, so the edge you climb FROM
 * is u1 going down-axis and u0 going up it -- that is where the riser stands
 * and where the tread oversails it.  The riser is planted 8 mm proud of the
 * carriage rather than flush with it, because flush means coplanar with the
 * face behind it and that seam shimmers the length of the flight; the tread
 * still overhangs the riser's own face by the nosing.
 *
 * `base` is where the collider's underside goes, and it is the only thing
 * that differs between the two flights: the low one fills to the floor, the
 * long one stops one rise down so the flight tiles without closing the space
 * beneath it.
 */
function step(b, axis, u0, u1, w0, w1, top, base, dir) {
  const wc = (w0 + w1) / 2, wl = w1 - w0;
  const over = RISER_OUT + NOSING;
  const un = dir < 0 ? u1 : u0;                  // the edge you climb from
  along(b, axis, (u0 + u1) / 2 - dir * over / 2, top - TREAD_T, wc,
        u1 - u0 + over, TREAD_T, wl, FB, BOARD);
  along(b, axis, un + dir * (RISER_T / 2 - RISER_OUT), top - RISE, wc,
        RISER_T, RISE - TREAD_T, wl - 0.06, TR);
  alongCollider(b, axis, (u0 + u1) / 2, base, wc, u1 - u0, top - base, wl);
}

/**
 * A board laid parallel to the flight's pitch, `t` deep perpendicular to it,
 * `yTop` giving the height of its top surface at z.  The -Z end rises,
 * which is the way this flight climbs.
 *
 * SHEARED rather than rotated, which is the one place this file does not
 * follow shell.js's shedRoof and deck.js's slopedBoardX.  Rotating a box
 * cuts its ends square to the pitch, and that is right for a roof, whose
 * ends are eaves hanging in the open air.  A string is cut PLUMB at both
 * ends, and here the difference is not a detail: a 0.34 m board cut square
 * at the head throws its lower corner 0.20 m past it, out of the well and
 * down through the dining-room ceiling.  The shear leaves the ends vertical
 * and the board exactly z0..z1, so the strings die into the trimmer where
 * they should.
 */
function raked(b, x, z0, z1, yTop, w, t, mat) {
  const h = t / Math.cos(PITCH);              // depth, measured plumb
  const g = new THREE.BoxGeometry(w, h, z1 - z0);
  g.applyMatrix4(new THREE.Matrix4().set(
    1, 0, 0, 0, 0, 1, -TAN, 0, 0, 0, 1, 0, 0, 0, 0, 1));
  g.translate(x, (yTop(z0) + yTop(z1)) / 2 - h / 2, (z0 + z1) / 2);
  b.add(g, mat, { collider: null, tile: 0.9, ao: 0.04, tint: 0 });
}

// ---------------------------------------------------------------------------
/**
 * The three risers off the hall: two treads climbing +X, then the landing.
 *
 * Each step is a plastered box with a board on it, not a tread on strings.
 * These two stand out in the middle of the hall with both flanks showing --
 * they are what the plan draws swinging out into it -- and a raking string
 * over a solid podium is the thing that does not work there: the string's
 * bottom edge falls away from the floor as it climbs, so it would leave a
 * growing triangle of hole under itself, and a stair with a gap under its
 * flank reads as a mistake from every angle in an open hall.  Boxed in, the
 * flanks are simply the sides of what the steps are made of.
 */
function lowFlight(b) {
  const L = S.low;
  const zc = (L.z0 + L.z1) / 2, zd = L.z1 - L.z0;
  for (let k = 1; k <= L.treads; k++) {
    const u0 = L.nose + (k - 1) * GOING, u1 = L.nose + k * GOING;
    const top = S.bottom + k * RISE;
    along(b, "x", (u0 + u1) / 2, F, zc, u1 - u0, top - TREAD_T - F, zd, PL);
    step(b, "x", u0, u1, L.z0, L.z1, top, F, +1);
  }
}

/**
 * The quarter-space landing, which is also the third riser's tread.
 *
 * Boarded the way interior.js boards the floor -- 0.19 m pitch, every third
 * one the shaded batch -- because it is a floor you stand on and, through
 * the well, the first thing anyone upstairs looks down at.  Its nosing is on
 * the -X edge, over the riser the low flight arrives at; its +Z face is the
 * one the hall sees and its -Z face closes the top of the understairs.  One
 * collider from the floor to the walking surface: solid, for the reason at
 * the top of this file.
 */
function landing(b) {
  const L = S.landing;
  const xc = (L.x0 + L.x1) / 2, xw = L.x1 - L.x0;
  const zc = (L.z0 + L.z1) / 2, zd = L.z1 - L.z0;
  const over = RISER_OUT + NOSING;
  b.block(xc, F, zc, xw, L.y - TREAD_T - F, zd, PL, FLAT);

  const n = Math.round(zd / 0.19);
  for (let i = 0; i < n; i++) {
    b.block(xc, L.y - TREAD_T, L.z0 + (i + 0.5) * (zd / n), xw, TREAD_T,
            zd / n - 0.008, i % 3 === 2 ? FS : FB, BOARD);
  }
  b.block(L.x0 + (0.10 - over) / 2, L.y - TREAD_T, zc, 0.10 + over, TREAD_T,
          zd, FB, BOARD);
  b.block(L.x0 + RISER_T / 2 - RISER_OUT, L.y - RISE, zc, RISER_T,
          RISE - TREAD_T, zd - 0.06, TR, FLAT);

  b.collider(xc, F, zc, xw, L.y - F, zd);
}

/**
 * The straight flight: eleven treads climbing -Z with the garage wall at
 * their back.
 *
 * The eleventh tops out at 3.303 and the fifteenth riser is climbed onto
 * upstairs.js's boards, so the last riser board is built here and the tread
 * over it is not -- that tread is the floor of the storey above.
 */
function upperFlight(b) {
  const G = S.flight;
  const xc = (G.x0 + G.x1) / 2, xw = G.x1 - G.x0;

  for (let k = 1; k <= G.treads; k++) {
    const u1 = G.nose - (k - 1) * GOING, u0 = G.nose - k * GOING;
    step(b, "z", u0, u1, G.x0, G.x1, G.y0 + k * RISE, G.y0 + (k - 1) * RISE,
         -1);
  }
  const last = G.y0 + G.treads * RISE;              // 3.303
  b.block(xc, last, G.head + RISER_T / 2, xw - 0.06, S.top - last, RISER_T,
          TR, FLAT);

  // THE SOFFIT, and it is the whole underside of the flight -- a millimetre
  // wider than the treads at both edges, so that looking up from the dining
  // room there is one raking plaster plane and not a single tread, riser or
  // string bottom in sight.  That millimetre is also why it is 1 mm PROUD of
  // the tread ends rather than flush with them: flush is coplanar.
  //
  // MATS.ceiling, not plaster and never trim.  The scene's fill is a
  // hemisphere whose lower half is lawn-coloured, so a downward face in any
  // ordinary material comes out olive -- the trap interior.js calls out for
  // its door headers, and under a 3 m flight it is the size of a wall.
  //
  // 12 mm inside the head, where the trimmer face below swallows the end: a
  // board ending ON z = head puts its end face in the same plane and facing
  // the same way as that face, and two coplanar faces that both look at you
  // is the one seam the merge cannot hide.
  raked(b, xc, G.head + 0.012, G.nose + 0.03, soffitY, xw + 0.002,
        SOFFIT_T, PC);

  // The trimmer face: the joist zone between the soffit's top end and the
  // underside of the last tread, otherwise a slot looking up into the floor
  // structure from the far end of the dining room.  Ceiling material for the
  // same reason the soffit is -- its own underside is part of that view.
  b.block(xc, last - TREAD_T - 0.06, G.head + 0.03, xw - 0.06, 0.08, 0.06,
          PC, FLAT);

  // Both strings run the whole flight and overshoot the floor above by the
  // 5 cm they stand over the nosings.  On the open side the head newel
  // covers that overshoot exactly; on the wall side it dies into the well's
  // corner, which is what a string does at a trimmer.  The wall string is
  // also that face's skirting -- a raking skirting board is a fiddly thing
  // done badly, and the string is the same board doing the same job.  They
  // start 8 mm further in than the soffit so their plumb ends do not share
  // its plane either.
  for (const x of [G.x1 - STRING_T / 2 + 0.001, GUARD_X]) {
    raked(b, x, G.head + 0.02, G.nose + 0.05, (z) => noseY(z) + STRING_UP,
          STRING_T, STRING_D, TR);
  }
}

// ---------------------------------------------------------------------------
/**
 * A newel: a painted post with a timber cap, and no collider of its own.
 */
function newel(b, x, z, base, top) {
  b.block(x, base, z, POST, top - base - 0.045, POST, TR, FLAT);
  b.block(x, top - 0.045, z, POST + 0.04, 0.045, POST + 0.04, TM, FLAT);
}

/**
 * A level guard: shoe rail, handrail and spindles between two newels.
 *
 * The spindles stand on the shoe rather than on the floor, because the runs
 * upstairs sit half a post inside the well edge -- so the shoe is what
 * carries their feet across the opening, and without it they end in mid-air
 * over the trimmer.  `hollow` says whether that is the case: a run standing
 * on the landing has a floor under its shoe and wants no skin beneath it.
 */
function guardRun(b, axis, a0, a1, at, base, hollow = true) {
  const len = a1 - a0;
  if (len < 0.1) return;
  const box = (p, y, l, h, t, mat) => axis === "x"
    ? b.block(p, y, at, l, h, t, mat, FLAT)
    : b.block(at, y, p, t, h, l, mat, FLAT);
  box((a0 + a1) / 2, base, len, 0.06, 0.075, TR);
  box((a0 + a1) / 2, base + RAIL_H - 0.055, len, 0.055, 0.075, TM);
  // The rail is 0.85 up in the air whatever it stands on, and a shoe over
  // the well is a face the room below looks straight up at.
  for (const y of hollow ? [base, base + RAIL_H - 0.055]
                         : [base + RAIL_H - 0.055])
    box((a0 + a1) / 2, y - SKIN + 0.002, len, SKIN, 0.079, PC);
  const n = Math.max(1, Math.round(len / BAL_PITCH) - 1);
  for (let i = 1; i <= n; i++)
    box(a0 + (len * i) / (n + 1), base + 0.06, BAL, RAIL_H - 0.115, BAL, TR);
}

/**
 * The balustrade: raked up the flight's open west side, level along the
 * landing's hall face, and level round the two edges of the well that are
 * neither the wall nor the way off the stair.
 *
 * All of it in the GUARD_X plane bar the two cross runs, which is what lets
 * the rake run into the level rail without a jog: at the head the nosing
 * line IS 3.50, so a rail 0.90 over the nosings and a rail 0.90 over the
 * floor arrive at the same 4.40 and the head newel stands where they meet.
 *
 * The raked spindles are all one length.  Two parallel lines 0.90 apart do
 * not care where you cut them, so the height is written once and there is no
 * per-baluster arithmetic at all.
 *
 * Upstairs, the well's east edge is the garage wall and needs nothing, and
 * its z0 end is deliberately left open: that is the head of the flight, the
 * full 1.24 m you walk off on to the boards.  Guarding it would wall the
 * stair off from the storey it climbs to.  Its z1 end -- 2.36 m over the
 * landing -- gets the run that edge does need.
 *
 * Nothing here is solid, so the two newels standing in the landing's west
 * edge, one at each end of the way up from the hall, cannot be walked into.
 */
function balustrade(b) {
  const G = S.flight, L = S.landing;
  const foot = G.nose + 0.05;                   // newel stands on the landing
  const headZ = W.z0 + POST / 2;
  const top = UP.floor + RAIL_H + CAP;

  newel(b, GUARD_X, foot, L.y, railY(G.nose) + CAP);
  newel(b, GUARD_X, GUARD_Z_LOW, L.y, L.y + RAIL_H + CAP);
  newel(b, GUARD_X, headZ, UP.floor, top);
  newel(b, GUARD_X, GUARD_Z, UP.floor, top);

  // Both rails stop 20 mm INSIDE their newels rather than against them, for
  // the reason the strings do: a rail cut off in the plane of the post's own
  // outer face fights it.
  const r0 = W.z0 + 0.02, r1 = foot + POST / 2 - 0.02;
  raked(b, GUARD_X, r0, r1, railY, 0.075, 0.055, TM);
  raked(b, GUARD_X, r0, r1, (z) => railY(z) - 0.055 + 0.002, 0.079, SKIN, PC);
  const a0 = W.z0, len = foot - POST / 2 - a0;
  const n = Math.max(1, Math.round(len / BAL_PITCH) - 1);
  for (let i = 1; i <= n; i++) {
    const z = a0 + (len * i) / (n + 1);
    b.block(GUARD_X, noseY(z) + 0.04, z, BAL, RAIL_H - 0.095, BAL, TR, FLAT);
  }

  // The landing's hall face, and the two upstairs runs.  Each dies 10 mm
  // into the garage wall at its east end rather than stopping on its face.
  guardRun(b, "x", GUARD_X + POST / 2 - 0.01, G.x1 + 0.01, GUARD_Z_LOW, L.y,
           false);
  guardRun(b, "z", headZ + POST / 2 - 0.01, GUARD_Z - POST / 2 + 0.01,
           GUARD_X, UP.floor);
  guardRun(b, "x", GUARD_X + POST / 2 - 0.01, G.x1 + 0.01, GUARD_Z, UP.floor);
}

// ---------------------------------------------------------------------------
/**
 * One sliding leaf: a painted frame with a mirror panel set into it.
 *
 * The mirror stands 9 mm proud of the leaf and is buried 3 mm into it, so
 * the frame reads as a frame and no two faces are ever coplanar.
 *
 * The collider is the door PLANE, 0.09 deep, rather than the 0.04 leaf in
 * its own track: the two tracks are 45 mm apart, and a cat that can stand in
 * the 45 mm between two doors is a cat standing inside the joinery.
 */
function leaf(b, x, z0, z1) {
  const zc = (z0 + z1) / 2, w = z1 - z0;
  const y0 = CL.floor + 0.024, h = CL.head - 0.05 - y0;
  b.block(x + 0.02, y0, zc, 0.04, h, w, TR, FLAT);
  b.block(x - 0.003, y0 + 0.055, zc, 0.012, h - 0.11, w - 0.11, MIRROR,
          { solid: false, ao: 0, tint: 0, tile: 0 });
  b.collider(CL.x0 + 0.045, CL.floor, zc, 0.09, CL.head - CL.floor, w);
}

/**
 * The mirrored closet: first thing on your right through the front door.
 *
 * 0.59 m of carcass against the garage wall, which is the depth of a coat's
 * shoulders and the reason this is a slider and not a swing door.  The back
 * is that wall itself -- interior.js already stands plaster at PLAN.divX
 * across this whole stretch, between the front elevation and the garage
 * doorway, and already gives it a collider from the floor to the ceiling --
 * so there is no back panel here and no back collider either; building one
 * would be a second wall 6 cm from the first.  The +Z return is the front
 * wall of the house for the same reason: its plaster face is at 1.17 and the
 * closet stops at 1.16.
 *
 * One leaf stands open, and that is the whole design.  Two 0.73 m leaves
 * exactly fill the 1.46 m opening, so shut, this is a wall with a mirror on
 * it and nothing a cat can do about it; slid 0.50 m toward the front door,
 * the back leaf leaves an opening a cat walks straight into without a step,
 * a squeeze or a jump.
 */
function closet(b) {
  const xc = (CL.x0 + CL.x1) / 2, cd = CL.x1 - CL.x0;
  const zc = (CL.z0 + CL.z1) / 2, cw = CL.z1 - CL.z0;
  const ret = 0.09;                               // the one return it needs

  b.block(xc, CL.floor, CL.z0 - ret / 2, cd, CL.head - CL.floor, ret, PL,
          { ao: 0.04, tint: 0 });
  // The bulkhead dies 3 cm INTO the ceiling slab, interior.js's TOP trick:
  // stopped flush at 3.35 its top face is coplanar with the ceiling's
  // underside, and that junction runs the length of the closet.
  b.block(xc, CL.head, (CL.z0 - ret + CL.z1) / 2, cd, CL.top + 0.03 - CL.head,
          cw + ret, PL, { ao: 0.04, tint: 0 });

  // Casings: head, and a jamb at each side of the opening.  Trim, planted on
  // the leaf line, no colliders -- the carcass behind them is the solid bit.
  b.block(CL.x0 - 0.0125, CL.head - 0.03, (CL.z0 - ret + CL.z1) / 2, 0.045,
          0.13, cw + ret, TR, FLAT);
  b.block(CL.x0 - 0.0125, CL.floor, CL.z0 - ret / 2, 0.045,
          CL.head + 0.10 - CL.floor, ret, TR, FLAT);
  b.block(CL.x0 - 0.0125, CL.floor, CL.z1 - 0.015, 0.045,
          CL.head + 0.10 - CL.floor, 0.05, TR, FLAT);

  // A board on the floor so the bay does not read as a hole in the boarding,
  // and 5 mm proud of it rather than flush: two coplanar floors shimmer, and
  // 5 mm is nothing to walk over.
  b.block(xc, CL.floor - 0.010, zc, cd, 0.015, cw, FS, BOARD);

  // Shelf and rail.  The shelf is the one piece in here with a collider: it
  // is a 1.85 m ledge in a half-metre slot, which is out of a plain 0.95
  // jump and inside a charged pounce, and a cat that finds it should land on
  // it rather than through it.  interior.js makes the same call for its
  // window sills and for nothing else.
  b.block(CL.x0 + 0.03 + (cd - 0.06) / 2, CL.shelf - 0.035, zc, cd - 0.06,
          0.035, cw, TR, { ao: 0.04, tint: 0 });
  // Skinned underneath: a cat standing in the open bay is looking straight
  // up at three quarters of a square metre of shelf, and in trim that reads
  // lawn-green.
  b.block(CL.x0 + 0.03 + (cd - 0.06) / 2, CL.shelf - 0.035 - SKIN + 0.002,
          zc, cd - 0.06 + 0.004, SKIN, cw + 0.004, PC, FLAT);
  b.bar(CL.x1 - 0.28, CL.rail - 0.016, zc, 0.016, cw, "z", MP,
        { solid: false, ao: 0.04, tint: 0 });

  // Head and bottom tracks, both leaf widths wide.  The head track's own
  // underside and the head casing's are in one plane at CL.head - 0.03, so
  // one skin covers the pair of them -- the whole 0.13 m the hall sees when
  // it looks up at the doorway.  The leaves stop 6 mm under it.
  b.block(CL.x0 + 0.0475, CL.head - 0.03, zc, 0.095, 0.03, cw, MP, FLAT);
  b.block(CL.x0 + 0.0475, CL.floor, zc, 0.095, 0.02, cw, MP, FLAT);
  b.block(CL.x0 + 0.03, CL.head - 0.044, (CL.z0 - ret + CL.z1) / 2, 0.126,
          SKIN, cw + ret - 0.004, PC, FLAT);

  leaf(b, CL.x0, CL.z1 - CL.leaf, CL.z1);                       // shut
  leaf(b, CL.x0 + 0.045, CL.z0 + CL.slide,                      // slid open
       CL.z0 + CL.slide + CL.leaf);
}

// ---------------------------------------------------------------------------
export function build(b) {
  lowFlight(b);
  landing(b);
  upperFlight(b);
  balustrade(b);
  closet(b);

  // The climb, bottom to top, and the one place in the closet worth naming.
  b.mark("stair.foot", S.low.nose - 0.25, S.bottom,
         (S.low.z0 + S.low.z1) / 2);
  b.mark("stair.landing", (S.landing.x0 + S.landing.x1) / 2, S.landing.y,
         (S.landing.z0 + S.landing.z1) / 2);
  b.mark("stair.head", (S.flight.x0 + S.flight.x1) / 2, S.top,
         S.flight.head - 0.20);
  b.mark("closet.inside", (CL.x0 + CL.x1) / 2, CL.floor,
         CL.z0 + CL.slide / 2);
}
